import { constants } from '@airtm/utils';
import * as yup from 'yup';
import { email, phone, phoneCountryCode, postalCode } from 'utils/schemaUtils';

const { REGEX_USERNAME } = constants;

export const passwordBaseSchema = (t, name) => {
  return yup
    .string()
    .ensure()
    .required(
      t('ERRORS:FORM_REQUIRED', {
        name,
      }),
    )
    .max(
      80,
      t('ERRORS:FORM_MAX_CHARACTERS', {
        name,
        max: 80,
      }),
    )
    .min(
      8,
      t('ERRORS:FORM_MIN_CHARACTERS', {
        name,
        min: 8,
      }),
    );
};

export function loginSchema(t) {
  return yup.object({
    email,
    password: yup
      .string()
      .required(t('ERRORS:FORM_REQUIRED', { name: t('LOGIN:CONFIRMATION_LABEL_PASSWORD') })),
  });
}

export const mfaCodeSchema = yup.object({
  code: yup.string().required().matches(/\d{6}/),
});

export const usernameSchema = yup
  .string()
  .required()
  .matches(REGEX_USERNAME)
  .test({
    name: 'username-includes-airtm',
    test(value) {
      return !value.toLowerCase().includes('airtm');
    },
  });

export const paymentMethodSearchSchema = (t) => {
  return yup.string().matches('^(?!.* {2})[0-9A-Za-zÀ-ÖØ-öø-ÿ ]{0,}$', {
    message: t('PAYMENT_METHODS:SEARCH_TEXT_INVALID'),
  });
};

export const reasonValidationSchema = (t) => {
  return yup.object().shape({
    reason: yup.string().required(() =>
      t('ERRORS:FORM_REQUIRED', {
        name: t('CANCELLATION_REASONS:LABEL_REASON'),
      }),
    ),
    comment: yup.string().when('reason', {
      is: 'CANCELLATION_REASONS:OTHER',
      then: yup
        .string()
        .required(() =>
          t('ERRORS:FORM_REQUIRED', {
            name: t('CANCELLATION_REASONS:LABEL_MORE_DETAILS'),
          }),
        )
        .min(10, () =>
          t('ERRORS:FORM_MIN_CHARACTERS', {
            name: t('CANCELLATION_REASONS:LABEL_MORE_DETAILS'),
            min: 10,
          }),
        ),
    }),
  });
};

export const exchangeAmountSchema = yup.number().positive().required();

const boundedNumberSchema = (max, min) => {
  let schema = yup.number().positive().max(max);
  if (min) schema = schema.min(min);
  return schema;
};

export function requestLoanSchema({ maxAmount, minAmount, maxDuration, minDuration }) {
  return yup.object({
    amount: boundedNumberSchema(maxAmount, minAmount).integer().required(),
    durationInDays: boundedNumberSchema(maxDuration, minDuration).integer().required(),
  });
}

export function repayLoanSchema(amountLeftToPay, walletAmount) {
  return yup.object({
    amount: boundedNumberSchema(Math.min(amountLeftToPay, walletAmount)),
  });
}
export const alfredPayAccountFieldsSchema = yup.object({
  alias: yup
    .string()
    .required('FORM_FIELDS:REQUIRED')
    .min(3, 'VIRTUAL_ACCOUNTS:CVU_DETAILS_ALIAS_ERR_LENGTH')
    .max(20, 'VIRTUAL_ACCOUNTS:CVU_DETAILS_ALIAS_ERR_LENGTH')
    .matches(/^[a-zA-Z0-9.]+$/, 'VIRTUAL_ACCOUNTS:CVU_DETAILS_ALIAS_ERR_CHARS'),
});

export function buildAddressAndPhoneSchema(needsAddress, needsPhone, t) {
  let schema = yup.object();

  if (needsAddress) {
    schema = schema.concat(
      yup.object({
        addressLine1: yup
          .string()
          .required(
            t('ERRORS:FORM_REQUIRED', { name: t('SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_1') }),
          ),
        city: yup
          .string()
          .required(t('ERRORS:FORM_REQUIRED', { name: t('SIGNUP:CONFIRMATION_LABEL_CITY') })),
        country: yup
          .string()
          .required(t('ERRORS:FORM_REQUIRED', { name: t('SIGNUP:CHOOSE_COUNTRY') })),
        addressLine2: yup.string(),
        postalCode,
      }),
    );
  }

  if (needsPhone) {
    schema = schema.concat(
      yup.object({
        phoneCountryCode,
        phone,
      }),
    );
  }

  return schema;
}
